import React from "react";
import {
  FaBell,
  FaCalendarAlt,
  FaCameraRetro,
  FaEnvelope,
  FaHandsHelping,
  FaMapMarkedAlt,
  FaMedal,
  FaNewspaper,
  FaServer,
  FaShieldAlt,
  FaUsers,
  FaUserTie,
} from "react-icons/fa";

import Reveal from "../../../components/ui/Reveal";
import SectionTitle from "./SectionTitle";

const FEATURES = [
  {
    icon: FaUsers,
    title: "Annuaire des associations",
    text: "Présentez clairement toutes les associations de votre commune avec leurs coordonnées, activités, lieux de pratique et informations utiles.",
    tag: "Habitants",
  },
  {
    icon: FaUserTie,
    title: "Fiches détaillées",
    text: "Chaque association dispose de sa propre page avec description, responsable, téléphone, email, site web, réseaux sociaux, horaires et présentation.",
    tag: "Associations",
  },
  {
    icon: FaCalendarAlt,
    title: "Événements associatifs",
    text: "Valorisez les forums, stages, portes ouvertes, animations, compétitions, spectacles, rencontres et rendez-vous importants.",
    tag: "Agenda",
  },
  {
    icon: FaNewspaper,
    title: "Actualités locales",
    text: "Les associations peuvent publier leurs informations importantes avec texte, image et contenu enrichi pour informer rapidement les habitants.",
    tag: "Communication",
  },
  {
    icon: FaCameraRetro,
    title: "Albums photos",
    text: "Mettez en valeur la vie locale grâce à des galeries photos simples à consulter : événements, activités, rencontres ou temps forts.",
    tag: "Valorisation",
  },
  {
    icon: FaMapMarkedAlt,
    title: "Carte interactive",
    text: "Les habitants retrouvent facilement une association, un lieu de pratique ou une infrastructure municipale directement sur une carte.",
    tag: "Localisation",
  },
  {
    icon: FaHandsHelping,
    title: "Partenaires et acteurs locaux",
    text: "Affichez les partenaires, soutiens ou structures associées à la vie sportive, culturelle et associative de votre commune.",
    tag: "Réseau local",
  },
  {
    icon: FaMedal,
    title: "Palmarès et réussites",
    text: "Mettez en avant les résultats sportifs, distinctions, réussites culturelles ou actions remarquables des associations.",
    tag: "Réussites",
  },
  {
    icon: FaEnvelope,
    title: "Formulaires de contact",
    text: "Les habitants peuvent contacter facilement l’association concernée ou le service responsable depuis une page simple et accessible.",
    tag: "Contact",
  },
  {
    icon: FaShieldAlt,
    title: "Administration par rôles",
    text: "La mairie garde le contrôle de l’ensemble du portail tout en donnant un accès limité aux responsables d’associations.",
    tag: "Sécurité",
  },
  {
    icon: FaBell,
    title: "Notifications et suivi",
    text: "Les messages reçus, contenus publiés et informations importantes peuvent être suivis depuis un tableau de bord administrateur.",
    tag: "Gestion",
  },
  {
    icon: FaServer,
    title: "Solution hébergée",
    text: "L’application est proposée avec l’hébergement inclus, pour une solution simple, prête à l’emploi et sans complexité technique pour la mairie.",
    tag: "Inclus",
  },
];

export default function AppFeatures() {
  return (
    <section className="bg-slate-50 py-16 dark:bg-slate-950 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionTitle
          eyebrow="Fonctionnalités"
          title="Tout ce qu’il faut pour "
          highlight="valoriser vos associations"
          subtitle="MonAppliAsso centralise les informations associatives de votre commune dans un portail clair, moderne et facile à administrer."
        />

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((feature, index) => {
            const Icon = feature.icon;

            return (
              <Reveal key={feature.title} delay={index * 0.03}>
                <article className="group relative h-full overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-xl dark:border-white/10 dark:bg-slate-900">
                  <div className="absolute right-0 top-0 h-24 w-24 rounded-bl-full bg-primary/5 transition group-hover:bg-primary/10" />

                  <div className="relative">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary transition group-hover:bg-primary group-hover:text-white">
                        <Icon size={20} />
                      </div>

                      <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600 dark:bg-white/10 dark:text-slate-300">
                        {feature.tag}
                      </span>
                    </div>

                    <h3 className="mt-5 text-xl font-bold text-slate-950 dark:text-white">
                      {feature.title}
                    </h3>

                    <p className="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-300">
                      {feature.text}
                    </p>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.18}>
          <div className="mt-8 rounded-[2rem] border border-primary/20 bg-white p-6 shadow-sm dark:border-primary/30 dark:bg-slate-900 sm:p-8">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-primary">
                  Pour la mairie
                </p>

                <h3 className="mt-2 text-2xl font-bold text-slate-950 dark:text-white">
                  Un portail associatif simple à gérer au quotidien
                </h3>

                <p className="mt-3 max-w-3xl text-sm leading-7 text-slate-600 dark:text-slate-300">
                  La mairie administre l’ensemble du site depuis un tableau de
                  bord dédié, tandis que chaque association peut disposer d’un
                  accès pour mettre à jour ses propres informations.
                </p>
              </div>

              <div className="grid gap-3 sm:grid-cols-3 lg:min-w-[460px]">
                <div className="rounded-2xl bg-slate-100 p-4 dark:bg-white/10">
                  <p className="text-2xl font-bold text-slate-950 dark:text-white">
                    1
                  </p>
                  <p className="mt-1 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    portail centralisé
                  </p>
                </div>

                <div className="rounded-2xl bg-slate-100 p-4 dark:bg-white/10">
                  <p className="text-2xl font-bold text-slate-950 dark:text-white">
                    260€
                  </p>
                  <p className="mt-1 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    par an tout compris
                  </p>
                </div>

                <div className="rounded-2xl bg-slate-100 p-4 dark:bg-white/10">
                  <p className="text-2xl font-bold text-slate-950 dark:text-white">
                    0
                  </p>
                  <p className="mt-1 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    complexité technique
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}